import { useAuth } from "@/_core/hooks/useAuth";
import { GymHeader } from "@/components/GymHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EVENTS, trackEvent, trackGymCheckIn } from "@/lib/tracking";
import { trpc } from "@/lib/trpc";
import { addHours, addMinutes, format, isAfter, isBefore } from "date-fns";
import { de } from "date-fns/locale";
import { Calendar, CheckCircle, Clock, Edit2, QrCode, Save, User, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useLocation } from "wouter";

export default function MyBookings() {
  const [, setLocation] = useLocation();
  const { user, loading: authLoading } = useAuth();
  const [cancelBookingId, setCancelBookingId] = useState<number | null>(null);
  const [checkInBookingId, setCheckInBookingId] = useState<number | null>(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileName, setProfileName] = useState("");
  const [profilePhone, setProfilePhone] = useState("");
  const [profileSmsConsent, setProfileSmsConsent] = useState(false);
  
  const utils = trpc.useUtils();

  const { data: config } = trpc.gym.getConfig.useQuery();
  
  const { data: confirmedBookings, isLoading: confirmedLoading } = trpc.gym.member.myBookings.useQuery(
    { status: "confirmed" },
    { enabled: !!user }
  );

  const { data: cancelledBookings, isLoading: cancelledLoading } = trpc.gym.member.myBookings.useQuery(
    { status: "cancelled" },
    { enabled: !!user }
  );

  const { data: todayBookings } = trpc.gym.member.todayBookings.useQuery(
    undefined,
    { enabled: !!user }
  );

  const { data: profile } = trpc.gym.member.profile.useQuery(
    undefined,
    { enabled: !!user }
  );

  // Initialize form values when profile loads
  useEffect(() => {
    if (profile && !isEditingProfile) {
      setProfileName(profile.name || "");
      setProfilePhone(profile.phone || "");
      setProfileSmsConsent(!!profile.smsConsent);
    }
  }, [profile, isEditingProfile]);

  const updateProfileMutation = trpc.gym.member.updateProfile.useMutation({
    onSuccess: () => {
      toast.success("Profil erfolgreich aktualisiert");
      setIsEditingProfile(false);
      utils.gym.member.profile.invalidate();
      utils.auth.me.invalidate();
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });

  const cancelMutation = trpc.gym.member.cancel.useMutation({
    onSuccess: () => {
      // Track gym booking cancellation
      trackEvent(EVENTS.GYM_BOOKING_CANCELLED, { booking_id: cancelBookingId });
      toast.success("Buchung erfolgreich storniert");
      setCancelBookingId(null);
      utils.gym.member.myBookings.invalidate();
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });

  const checkInMutation = trpc.gym.member.checkIn.useMutation({
    onSuccess: () => {
      // Track gym check-in
      const booking = confirmedBookings?.find(b => b.id === checkInBookingId);
      if (booking && booking.slot) {
        trackGymCheckIn(booking.id, booking.slot.id);
      }
      toast.success("Check-in erfolgreich!");
      setCheckInBookingId(null);
      utils.gym.member.myBookings.invalidate();
      utils.gym.member.todayBookings.invalidate();
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });

  const isGymMember = user?.roles?.includes("gym_member");
  const gymStatus = (user as any)?.gymStatus;

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background">
        <GymHeader />
        <div className="container py-12 text-center">
          <p className="text-muted-foreground">Laden...</p>
        </div>
      </div>
    );
  }

  if (!user || !isGymMember || gymStatus !== "approved") {
    return (
      <div className="min-h-screen bg-background">
        <GymHeader />
        <div className="container py-12 text-center">
          <h1 className="text-2xl font-bold">Zugang verweigert</h1>
          <p className="text-muted-foreground mt-4">
            Sie müssen ein freigeschaltetes Gym-Mitglied sein, um diese Seite zu sehen.
          </p>
          <Button className="mt-6" onClick={() => setLocation("/gym")}>
            Zurück zur Gym-Übersicht
          </Button>
        </div>
      </div>
    );
  }

  const upcomingBookings = confirmedBookings?.filter(b => 
    b.slot && isAfter(new Date(b.slot.startTime), new Date())
  ) || [];

  const pastBookings = confirmedBookings?.filter(b => 
    b.slot && isBefore(new Date(b.slot.endTime), new Date())
  ) || [];

  const canCancel = (booking: NonNullable<typeof confirmedBookings>[number]) => {
    if (!booking.slot) return false;
    const slotStart = new Date(booking.slot.startTime);
    const cancelDeadline = config?.gym?.cancellationDeadlineHours ?? 24;
    return isAfter(slotStart, addHours(new Date(), cancelDeadline));
  };

  const canCheckIn = (booking: NonNullable<typeof confirmedBookings>[number]) => {
    if (!booking.slot || booking.checkedInAt) return false;
    const now = new Date();
    const slotStart = new Date(booking.slot.startTime);
    const slotEnd = new Date(booking.slot.endTime);
    // Can check in 15 minutes before until slot ends
    const checkInStart = addMinutes(slotStart, -15);
    return now >= checkInStart && now <= slotEnd;
  };

  const selectedCancelBooking = confirmedBookings?.find(b => b.id === cancelBookingId);
  const selectedCheckInBooking = confirmedBookings?.find(b => b.id === checkInBookingId);

  return (
    <div className="min-h-screen bg-background">
      <GymHeader />
      
      <div className="container py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold">Meine Buchungen</h1>
            <p className="text-muted-foreground">
              Verwalten Sie Ihre Trainingsslots
            </p>
          </div>
          
          <Button onClick={() => setLocation("/gym/calendar")}>
            <Calendar className="h-4 w-4 mr-2" />
            Neuen Slot buchen
          </Button>
        </div>

        {/* Profile Management */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <User className="h-5 w-5" />
                <CardTitle>Meine Daten</CardTitle>
              </div>
              {!isEditingProfile ? (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setIsEditingProfile(true)}
                >
                  <Edit2 className="h-4 w-4 mr-2" />
                  Bearbeiten
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setIsEditingProfile(false);
                      if (profile) {
                        setProfileName(profile.name || "");
                        setProfilePhone(profile.phone || "");
                        setProfileSmsConsent(!!profile.smsConsent);
                      }
                    }}
                  >
                    Abbrechen
                  </Button>
                  <Button 
                    size="sm"
                    onClick={() => {
                      updateProfileMutation.mutate({
                        name: profileName,
                        phone: profilePhone,
                        smsConsent: profileSmsConsent
                      });
                    }}
                    disabled={updateProfileMutation.isPending}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Speichern
                  </Button>
                </div>
              )}
            </div>
            <CardDescription>
              Verwalten Sie Ihre persönlichen Informationen
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isEditingProfile ? (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="profile-name">Name</Label>
                  <Input
                    id="profile-name"
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="profile-email">E-Mail</Label>
                  <Input
                    id="profile-email"
                    value={user?.email || ""}
                    disabled
                    className="bg-muted"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    E-Mail kann nicht geändert werden
                  </p>
                </div>
                <div>
                  <Label htmlFor="profile-phone">Telefonnummer</Label>
                  <Input
                    id="profile-phone"
                    type="tel"
                    value={profilePhone}
                    onChange={(e) => setProfilePhone(e.target.value)}
                  />
                </div>
                <div className="flex items-start space-x-3 pt-2">
                  <Checkbox
                    id="profile-sms"
                    checked={profileSmsConsent}
                    onCheckedChange={(checked) => setProfileSmsConsent(checked as boolean)}
                  />
                  <div className="space-y-1 leading-none">
                    <Label
                      htmlFor="profile-sms"
                      className="text-sm font-normal cursor-pointer"
                    >
                      SMS-Benachrichtigungen erhalten
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Erhalten Sie Erinnerungen und wichtige Updates per SMS
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Name:</span>
                  <span className="text-sm font-medium">{profile?.name || "-"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">E-Mail:</span>
                  <span className="text-sm font-medium">{user?.email || "-"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Telefon:</span>
                  <span className="text-sm font-medium">{profile?.phone || "-"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">SMS-Benachrichtigungen:</span>
                  <Badge variant={profile?.smsConsent ? "default" : "secondary"}>
                    {profile?.smsConsent ? "Aktiviert" : "Deaktiviert"}
                  </Badge>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Today's Bookings */}
        {todayBookings && todayBookings.length > 0 && (
          <Card className="mb-8 border-primary">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                Heute
              </CardTitle>
              <CardDescription>
                Ihre Trainingsslots für heute
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {todayBookings.map(booking => (
                  <div 
                    key={booking.id}
                    className="flex items-center justify-between p-4 rounded-lg border"
                    style={{ 
                      borderLeftWidth: 4, 
                      borderLeftColor: booking.slot?.service?.color ?? "#888" 
                    }}
                  >
                    <div>
                      <div className="font-medium">{booking.slot?.service?.name}</div>
                      <div className="text-sm text-muted-foreground flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        {booking.slot && format(new Date(booking.slot.startTime), "HH:mm")} - {booking.slot && format(new Date(booking.slot.endTime), "HH:mm")}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {booking.checkedInAt ? (
                        <Badge variant="default" className="bg-green-500">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Eingecheckt
                        </Badge>
                      ) : canCheckIn(booking) ? (
                        <Button size="sm" onClick={() => setCheckInBookingId(booking.id)}>
                          <QrCode className="h-4 w-4 mr-2" />
                          Check-in
                        </Button>
                      ) : (
                        <Badge variant="secondary">Noch nicht möglich</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs defaultValue="upcoming">
          <TabsList>
            <TabsTrigger value="upcoming">
              Kommende ({upcomingBookings.length})
            </TabsTrigger>
            <TabsTrigger value="past">
              Vergangene ({pastBookings.length})
            </TabsTrigger>
            <TabsTrigger value="cancelled">
              Storniert ({cancelledBookings?.length ?? 0})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming" className="mt-6">
            {confirmedLoading ? (
              <div className="text-center py-8 text-muted-foreground">Laden...</div>
            ) : upcomingBookings.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Keine kommenden Buchungen</p>
                  <Button className="mt-4" onClick={() => setLocation("/gym/calendar")}>
                    Slot buchen
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {upcomingBookings.map(booking => (
                  <Card key={booking.id}>
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div 
                            className="w-2 h-12 rounded-full" 
                            style={{ backgroundColor: booking.slot?.service?.color ?? "#888" }}
                          />
                          <div>
                            <div className="font-medium">{booking.slot?.service?.name}</div>
                            <div className="text-sm text-muted-foreground">
                              {booking.slot && format(new Date(booking.slot.startTime), "EEEE, d. MMMM yyyy", { locale: de })}
                            </div>
                            <div className="text-sm text-muted-foreground flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {booking.slot && format(new Date(booking.slot.startTime), "HH:mm")} - {booking.slot && format(new Date(booking.slot.endTime), "HH:mm")}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {canCancel(booking) ? (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => setCancelBookingId(booking.id)}
                            >
                              <X className="h-4 w-4 mr-1" />
                              Stornieren
                            </Button>
                          ) : (
                            <Badge variant="secondary">
                              Nicht mehr stornierbar
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="past" className="mt-6">
            {pastBookings.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-muted-foreground">Keine vergangenen Buchungen</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {pastBookings.slice(0, 20).map(booking => (
                  <Card key={booking.id} className="opacity-75">
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div 
                            className="w-2 h-12 rounded-full" 
                            style={{ backgroundColor: booking.slot?.service?.color ?? "#888" }}
                          />
                          <div>
                            <div className="font-medium">{booking.slot?.service?.name}</div>
                            <div className="text-sm text-muted-foreground">
                              {booking.slot && format(new Date(booking.slot.startTime), "EEEE, d. MMMM yyyy", { locale: de })}
                            </div>
                            <div className="text-sm text-muted-foreground flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {booking.slot && format(new Date(booking.slot.startTime), "HH:mm")} - {booking.slot && format(new Date(booking.slot.endTime), "HH:mm")}
                            </div>
                          </div>
                        </div>
                        <div>
                          {booking.checkedInAt ? (
                            <Badge variant="default" className="bg-green-500">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Eingecheckt
                            </Badge>
                          ) : (
                            <Badge variant="secondary">Nicht erschienen</Badge>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="cancelled" className="mt-6">
            {cancelledLoading ? (
              <div className="text-center py-8 text-muted-foreground">Laden...</div>
            ) : !cancelledBookings || cancelledBookings.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-muted-foreground">Keine stornierten Buchungen</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {cancelledBookings.slice(0, 20).map(booking => (
                  <Card key={booking.id} className="opacity-60">
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div 
                            className="w-2 h-12 rounded-full bg-muted" 
                          />
                          <div>
                            <div className="font-medium line-through">{booking.slot?.service?.name}</div>
                            <div className="text-sm text-muted-foreground">
                              {booking.slot && format(new Date(booking.slot.startTime), "EEEE, d. MMMM yyyy", { locale: de })}
                            </div>
                            {booking.cancelledReason && (
                              <div className="text-sm text-muted-foreground">
                                Grund: {booking.cancelledReason}
                              </div>
                            )}
                          </div>
                        </div>
                        <Badge variant="destructive">Storniert</Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Cancel Dialog */}
      <Dialog open={!!cancelBookingId} onOpenChange={(open) => !open && setCancelBookingId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Buchung stornieren</DialogTitle>
            <DialogDescription>
              Möchten Sie diese Buchung wirklich stornieren?
            </DialogDescription>
          </DialogHeader>
          
          {selectedCancelBooking && (
            <div className="py-4 space-y-3">
              <div className="flex items-center gap-3">
                <div 
                  className="w-4 h-4 rounded-full" 
                  style={{ backgroundColor: selectedCancelBooking.slot?.service?.color ?? "#888" }}
                />
                <span className="font-medium">{selectedCancelBooking.slot?.service?.name}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="h-4 w-4" />
                {selectedCancelBooking.slot && format(new Date(selectedCancelBooking.slot.startTime), "EEEE, d. MMMM yyyy", { locale: de })}
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="h-4 w-4" />
                {selectedCancelBooking.slot && format(new Date(selectedCancelBooking.slot.startTime), "HH:mm")} - {selectedCancelBooking.slot && format(new Date(selectedCancelBooking.slot.endTime), "HH:mm")}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelBookingId(null)}>
              Abbrechen
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => cancelBookingId && cancelMutation.mutate({ bookingId: cancelBookingId })}
              disabled={cancelMutation.isPending}
            >
              {cancelMutation.isPending ? "Wird storniert..." : "Stornieren"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Check-in Dialog */}
      <Dialog open={!!checkInBookingId} onOpenChange={(open) => !open && setCheckInBookingId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Self-Service Check-in</DialogTitle>
            <DialogDescription>
              Bestätigen Sie Ihren Check-in für diesen Trainingsslot
            </DialogDescription>
          </DialogHeader>
          
          {selectedCheckInBooking && (
            <div className="py-4 space-y-3">
              <div className="flex items-center gap-3">
                <div 
                  className="w-4 h-4 rounded-full" 
                  style={{ backgroundColor: selectedCheckInBooking.slot?.service?.color ?? "#888" }}
                />
                <span className="font-medium">{selectedCheckInBooking.slot?.service?.name}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="h-4 w-4" />
                {selectedCheckInBooking.slot && format(new Date(selectedCheckInBooking.slot.startTime), "HH:mm")} - {selectedCheckInBooking.slot && format(new Date(selectedCheckInBooking.slot.endTime), "HH:mm")}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCheckInBookingId(null)}>
              Abbrechen
            </Button>
            <Button 
              onClick={() => checkInBookingId && checkInMutation.mutate({ bookingId: checkInBookingId })}
              disabled={checkInMutation.isPending}
            >
              {checkInMutation.isPending ? "Check-in läuft..." : "Jetzt einchecken"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
